import { TradeStatus } from "./TradeStatus"
import { FundID } from '@/api/funds/enum'
import { Holder, IHolderBankAccount, Shareholder } from './FormAccount'

export enum AccountType {
  /** 個人投資者 */
  Personal = 'Individual Account',
  /** 聯名戶 */
  Joint = 'Joint Account',
  /** 機構投資者 */
  Institutional = 'Institutional Account',
  /** 機構投資者 - Nominee */
  Nominee = 'Nominee Institutional Account',
  /** UG聯名戶 */
  UGJoint = 'UG Joint',
}

/** UG 聯名戶固定使用的 Agent ID */
export const UG_JOINT_AGENT_ID = 35
/** UG 主帳戶固定使用的 Holder PID */
export const UG_MAIN_HOLDER_PID = 734
/** UG 聯名戶固定的主認購人名稱 */
export const UG_JOINT_HOLDER_NAME = 'UG Investment Advisers Ltd.'

/** 是機構帳戶 */
export function isInstitutionalAccount(type?: AccountType) {
  return type === AccountType.Institutional || type === AccountType.Nominee
}
/** 是聯名戶 */
export function isJointAccount(type?: AccountType) {
  return type === AccountType.Joint
}

export interface ITradeCommon {
  /** 訂單交易編號(唯一值) */
  pno: number
  /** 訂單組編號(唯一值) */
  sno: number
  /** 此交易存續期間在訂單組內不可變的序號；中間缺口不回填，已刪除的尾端可重用 */
  trade_sequence: number
  /** 交易代碼 */
  trade_id: number
  /** 交易序號 */
  serial_number: string
  /** sub sales */
  // agent_name: string (已棄用)
  /** 2025-07 修訂：交易序號後面 (xxx) 的內容，不含括號 */
  serial_salename?: string
  /** 目前進度 */
  status: TradeStatus
  /** 伺服器依目前狀態與權限判斷，可否修改此序號水單。 */
  can_upload_payment_proof?: boolean
  /** 進度更新時間, ISO 8601 */
  updated_at: string
  /** 建立日, ISO 8601 */
  created_at: string
  /** Holder Name */
  holder_name: string
  /** HSBC Client ID(一定會有值) */
  hsbc_client_id?: string
  /** HSBC Holder ID (不一定有值) */
  hsbc_holder_id?: string
  /** BNP Holder ID (不一定有值) */
  bnp_holder_id?: string
  /** Cash Recept File Url (不一定有值) */
  file_cash_receipt?: string
  /** Cash Paid File Url (不一定有值) */
  file_cash_paid?: string
  /** Contract Note File Url (不一定有值) */
  file_contract_note?: string
  /** Contract Note 是最終版，若為轉換交易，可能檔案仍未是最終版 */
  contract_note_final?: boolean
  /** 正本提交通知發送次數 (不一定有值) */
  notify_file_sent?: number
  /** 贖回放款通知發送次數 (不一定有值) */
  notify_refund_sent?: number
  /** v2.2.2 是否有交易表單 */
  has_form?: boolean
  /** v2.2.2 此交易對應的客戶 */
  account_id?: number
  /** v2.2.2 退件說明 */
  rejection_detail?: {
    /** 檔案 url (退件說明) */
    file_url?: string
    /** 原始上傳檔案名稱 */
    file_name?: string
    /** 退件原因 */
    reason?: string
    /** 需要重送紙本 */
    need_resend_papers: boolean
    /** 需要重新上傳文件 */
    need_reupload_files: boolean
  }
  /** 未優先贖回 C Class 庫存 */
  should_redeem_class_c?: boolean
  /** 需審核 */
  review_required?: boolean
  /** 信託編號 */
  trust_code?: string
}

interface ITradeSingleTransaction {
  /** 交易類型 */
  dealing_type: 'SBS' | 'RDM' | 'SWITCH'
  /** 交易的基金 */
  fund_id: string
  /** 交易的基金：級別 */
  fund_class: string
  /** 交易日期 */
  trade_date: string
  /** 單位賣出價 (認購用) */
  trade_date_sell_price: number| null
  /** 單位買入價 (贖回用) */
  trade_date_buy_price: number| null
}

export interface ITradeSubscribe extends ITradeCommon, ITradeSingleTransaction {
  /** 認購金額(USD) */
  purchase: number
  /** 認購手續費(USD) */
  purchase_fees: number
  /** 認購費率 (1 = 100%) */
  purchase_fees_rate?: number
  /** Nominee 此筆認購是否含中國籍最終受益人 */
  is_beneficiary_in_china?: boolean | null
  /** Nominee 此筆認購中國籍最終受益人人數 */
  china_beneficiary_count?: number | null
}


export interface ITradeRedeem extends ITradeCommon, ITradeSingleTransaction {
  /** 贖回金額(USD)*/
  redeem: number
  /** 贖回股數 */
  redeem_quantity: number
  /** 贖回費率 (1 = 100%) */
  redeem_fees_rate: number
  /** 非 Nominee 此筆交易的客戶是否為中國籍 */
  is_beneficiary_in_china?: boolean | null
  /** 指定贖回 (Nominee Only) */
  redeem_from: IFormRedeemInventory[]
}

export interface ITradeSwitch extends ITradeCommon, ITradeSubscribe, ITradeRedeem {
  /** 原始申購日期 */
  original_subscription_date: string
  /** 「轉換」 switchin 訂單編號 */
  switchin_id: number
  /** 「轉換」 switchout 訂單編號 */
  switchout_id: number
  /** 轉出基金日 */
  switch_out_date: string
  /** 轉入基金日 */
  switch_in_date: string
  /** 轉出基金 */
  switch_out: string
  /** 轉入基金 */
  switch_in: string
  /** normal / same month */
  switch_type: 'normal' | 'same month'
  /** 是否沿用原始交易日 */
  carry_fwd: boolean
  /** 購入股數 */
  purchase_quantity: number
  /** 單位買入價 (轉出用) */
  switch_out_buy_price: number| null
  /** 單位賣出價 (轉入用) */
  switch_in_sell_price: number| null
}

export type ITrade = ITradeSubscribe | ITradeRedeem | ITradeSwitch

export interface IShareHolderInfo {
  name: string
  /** 手續費加權平均 (1 = 100%)，尚未回報則為 null */
  fee_rate?: number
  /** ISO 8601，尚未回報則為 null */
  fee_date?: string
  /** 庫存表檔案 url */
  inventory_file_url?: string
}

export interface ITradeHistory {
  id: number
  status: TradeStatus
  time: string
  /** 附件檔案 url */
  file_url?: string
  /** 附件檔案名稱 */
  file_name?: string
  detail?: {
    /** 退件原因 */
    reason?: string
    /** 需要重送紙本 */
    need_resend_papers?: boolean
    /** 需要重新上傳文件 */
    need_reupload_files?: boolean
  }
}

export interface IHolderAccountCommon {
  account_id: number
  /** 帳戶類型 */
  account_type: AccountType
  /** 初次進行線上交易 */
  initial: boolean
  /** UG holder_pid(系統辨識用-唯一值) */
  holder_pid: number
  /** 登入帳號 */
  usercode: string
  /** HSBC Client ID(一定會有值) */
  hsbc_client_id?: string
  /** HSBC Holder ID (不一定有值) */
  hsbc_holder_id?: string
  /** BNP Holder ID (不一定有值) */
  bnp_holder_id?: string
}

export interface IHolderAccountListItem extends IHolderAccountCommon {
  /** 帳戶名稱 */
  account_name: string
  /** 帳戶持有者 */
  holders: string[]
  /** 是否為草稿客戶 */
  draft: boolean
  /** 是否可贖回 */
  redeemable: boolean
}

export interface IHolderAccount extends IHolderAccountCommon {
  holder: Holder | null
  shareholders?: Shareholder[]
  /** 銀行帳戶資訊 v3.0 從 Holder 拆分到此 */
  bank: IHolderBankAccount
  /** 此客戶有幾個未遞件的交易草稿 (draft, apply_rejected) */
  draft_trades: number[]
  /** 曾經擁有過的基金 */
  owned_funds: FundID[]
  /** 目前仍持有的基金 */
  has_funds: FundID[]
  /** 無庫存，且未持有任何基金超過一年 */
  inactive: boolean
  /** 正式帳戶資料最後寫入時間 */
  formal_submitted_at?: string | null
  /** 既有客戶匯入參考資料(only when initial: true) */
  initial_data?: {
    field: string,
    value: string | null
    ord?: number | null
    rpt_date?: string | null
    upd_datetime?: string | null
  }[]
}

/** 可贖回庫存 */
export interface IRedeemableInventory {
  agent_id: number
  sales_id: number
  account_id: number
  /** 庫存編號 （or other key) */
  // inventory_id: number
  inventory_month: string
  /** 庫存基金 */
  fund_id: FundID
  /** 原申購日期 */
  original_subscription_date: string
  /** 預估贖回手續費率 1 = 100% */
  estimate_redemption_charge: number
  /** 目前持有股數 */
  shares: number
  /** 未確認贖回股數（API 回傳所有草稿與已送件占用量） */
  unconfirmed_redeem_shares: number
  /** 平均成本 */
  average_cost: number
  /** 參考單位淨值 */
  nav_per_share: number
  /** 參考市值 */
  market_value: number
  /** 未實現 參考累計損益 */
  unrealize_pnl: number
  /** 未實現 參考投資報酬率 1 = 100% */
  unrealize_perf: number
  /** 累計(未實現+已實現) 參考累計損益 */
  accumulate_pnl: number
  /** 累計(未實現+已實現) 參考投資報酬率 */
  accumulate_perf: number
  /** 買入日 Pension */
  dealing_date?: string | null
  /** 此筆庫存是否為中國籍交易 */
  is_beneficiary_in_china?: boolean | null
}

export type ChinaInventoryKeySource = {
  /** 庫存月份；指定贖回 API 以此識別保銀庫存。 */
  inventory_month: string
  /** 此筆庫存是否屬於中國籍交易；同一庫存月份可能拆成中國籍/非中國籍兩筆。 */
  is_beneficiary_in_china?: boolean | null
  /** Nominee 此筆認購中國籍最終受益人人數 */
  china_beneficiary_count?: number | null
  /** 基金代碼；同一畫面可能持有多基金庫存，組 key 時需避免跨基金撞號。 */
  fund_id?: FundID | null
}

/**
 * 建立指定贖回庫存的前端識別 key。
 *
 * 後端可能將相同基金、相同原申購月份的庫存拆成中國籍與非中國籍兩筆，
 * 因此前端選取與輸入狀態不能只使用 inventory_month，必須把基金與中國籍狀態一併納入。
 */
export function getRedeemInventoryKey(item: ChinaInventoryKeySource, fallbackFundId?: FundID | null): string {
  const fundKey = item.fund_id ?? fallbackFundId ?? ''
  return `${fundKey}:${item.inventory_month}:${item.is_beneficiary_in_china ? 'china' : 'non-china'}`
}

export type IFormRedeemInventory = {
  inventory_month: string
  /** 贖回股數 */
  shares: number
  /** 該庫存原申購日期 */
  meta_original_subscription_date: string
  /** 買入日 Pension */
  dealing_date?: string | null
  /** 此筆指定贖回庫存是否為中國籍交易 */
  is_beneficiary_in_china?: boolean | null
  /** Nominee 此筆認購中國籍最終受益人人數 */
  china_beneficiary_count?: number | null
}

/** 交易表單項目：贖回申請 */
export interface ITradeFormRedeem {
  /** 贖回交易日 YYYY-MM-DD */
  redeem_date: string
  /** 贖回基金 */
  redeem_fund: FundID | null
  /** 贖回方式 */
  redeem_method: null | 'Shares' | 'Amount' | 'Inventory'
  /** 贖回金額 */
  redeem_amount: null | number
  /** 贖回股數 */
  redeem_shares: null | number
  /** 贖回手續費 */
  redeem_fee_rate: null | number,
  /** 指定贖回 (Nominee Only) */
  redeem_from: IFormRedeemInventory[]
}

/** 帳戶所關聯的 Agent, Sub-sales */
export interface IRelativeAgent {
  name: string
  agent_id: number
  nominee?: boolean
  sub_sales: (IRelativeSales & {
    redeemable?: boolean
  })[]
  buyable_funds: FundID[]
}

/** 遞單管理關聯的 Sub-sales */
export interface IRelativeSales {
  name: string
  display_name?: string
  internal_name?: string,
  sales_id: number
  feat_ug_related?: boolean
  feat_redeem_fee_waived?: boolean
  feat_redeem_specified?: boolean
}
